import { IBetWayWinData } from "@nolimitcity/slot-game/bin/utils/server/IData";
import { gsap } from "gsap";
import { BitmapText, Container, Sprite, Texture } from "pixi.js";

import { AbstractSymbol } from "../../../../abstracts/elements/AbstractSymbol";
import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { symbolConfig } from "../../../UCConfig";
import { UgliestCatch } from "../../../UgliestCatch";
import { UCFontsNames, UCSpineNames } from "../../../assetsLoader/UCAssetsNames";
import {
    UCHighSymbolNames,
    UCLowSymbolNames,
    UCSpecialSymbolNames,
    UCSymbolNames,
    UCSymbolSize,
} from "../../../types/UCEnums";
import { symbolsResizeConfig } from "./UCSymbolsResizeConfig";

export const isFish = (symbolName: UCSymbolNames) => {
    return (
        symbolName === UCSpecialSymbolNames.F1 ||
        symbolName === UCSpecialSymbolNames.F2 ||
        symbolName === UCSpecialSymbolNames.F3 ||
        symbolName === UCSpecialSymbolNames.F4 ||
        symbolName === UCSpecialSymbolNames.F5 ||
        symbolName === UCSpecialSymbolNames.F6 ||
        symbolName === UCSpecialSymbolNames.F7 ||
        symbolName === UCSpecialSymbolNames.BIG_FISH
    );
};

export class UCSymbol extends AbstractSymbol {
    protected mainSpineMask: Sprite;
    protected topSeparator: Sprite;
    protected bottomSeparator: Sprite;
    protected winFrameSpine: AbstractSpine;
    protected winFrameDefaultHeight: number = 125;

    constructor(symbolName: string) {
        super(symbolName, UCSymbolSize.X1);
    }

    protected get symbolHeight(): number {
        return symbolConfig.symbolHeight;
    }

    set size(value: number | UCSymbolSize) {
        this._size = value > UCSymbolSize.X2 ? UCSymbolSize.X2 : value;
    }

    get size() {
        return this._size;
    }

    set isSpinning(value: boolean) {
        this._isSpinning = value;

        if (this.mainSpine && value) {
            this.mainSpine.reset();
            this.mainSpine.state.setAnimation(0, `Spin`, false);
        }
    }

    protected init(): void {
        this.topSeparator = new Sprite(Texture.from(`separator`));
        this.topSeparator.name = `topSeparator`;
        this.topSeparator.anchor.set(0, 0.5);
        this.topSeparator.x = -1.7; //symbolConfig.symbolWidth / 2 - this.topSeparator.width / 2 + 2;

        this.bottomSeparator = new Sprite(Texture.from(`separator`));
        this.bottomSeparator.name = `bottomSeparator`;
        this.bottomSeparator.anchor.set(0, 0.5);
        this.bottomSeparator.x = this.topSeparator.x;

        if (
            Object.values(UCLowSymbolNames).includes(this.name as UCLowSymbolNames) ||
            Object.values(UCHighSymbolNames).includes(this.name as UCHighSymbolNames)
        ) {
            this.winFrameSpine = new AbstractSpine(UCSpineNames.WIN_FRAME);
            this.winFrameSpine.x = symbolConfig.symbolWidth / 2;
            this.winFrameSpine.y = symbolConfig.symbolHeight / 2 + 3;
        }

        this.generateSymbol();
    }

    protected generateSymbol(): void {
        if (
            Object.values(UCLowSymbolNames).includes(this.name as UCLowSymbolNames) ||
            Object.values(UCHighSymbolNames).includes(this.name as UCHighSymbolNames) ||
            this.name === UCSymbolNames.F1 ||
            this.name === UCSymbolNames.F2 ||
            this.name === UCSymbolNames.F3 ||
            this.name === UCSymbolNames.F4 ||
            this.name === UCSymbolNames.F5 ||
            this.name === UCSymbolNames.F6 ||
            this.name === UCSymbolNames.F7 ||
            this.name === UCSymbolNames.SCATTER ||
            this.name === UCSymbolNames.WILD
        ) {
            const spineName: string = `spine/${this.name}.json`;

            this.mainSpine = new AbstractSpine(spineName);
            this.mainSpine.getSpineTween(`Normal`);

            this.mainSpine.x =
                symbolConfig.symbolWidth / 2 +
                symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetX!;
            this.mainSpine.y =
                symbolConfig.symbolHeight / 2 +
                symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetY!;
            this.addChild(this.mainSpine);
        } else {
            this.mainTextureSprite = new Sprite(Texture.from(this.name));
            this.mainTextureSprite.name = `mainTextureSprite`;
            this.mainTextureSprite.scale.set(0.5);

            this.mainTextureSprite.x =
                symbolConfig.symbolWidth / 2 - this.mainTextureSprite.width / 2;
            this.mainTextureSprite.y =
                symbolConfig.symbolHeight / 2 - this.mainTextureSprite.height / 2;
            this.mainTextureSprite.alpha = 1;

            this.addChild(this.mainTextureSprite);
        }
    }

    public getResizeAnimation(newSize: UCSymbolSize): gsap.core.Tween | gsap.core.Timeline {
        const animationDuration = 0.3;
        let newScale: number;
        let newX: number;
        let newY: number;

        switch (newSize) {
            case UCSymbolSize.X033:
                newScale = symbolsResizeConfig.get(this.name as UCSymbolNames)!.x033.scale!;
                newX = symbolsResizeConfig.get(this.name as UCSymbolNames)!.x033.offsetX!;
                newY = symbolsResizeConfig.get(this.name as UCSymbolNames)!.x033.offsetY!;

                break;

            case UCSymbolSize.X075:
                newScale = symbolsResizeConfig.get(this.name as UCSymbolNames)!.x075.scale!;
                newX = symbolsResizeConfig.get(this.name as UCSymbolNames)!.x075.offsetX!;
                newY = symbolsResizeConfig.get(this.name as UCSymbolNames)!.x075.offsetY!;

                break;

            //default is UCSymbolSize.X2
            default:
                newScale = symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.scale!;
                newX = symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetX!;
                newY = symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetY!;

                break;
        }

        this.mainSpineMask = new Sprite(Texture.WHITE);
        this.mainSpineMask.tint = Math.random() * 0xffffff;
        this.mainSpineMask.name = `textureMask`;
        this.mainSpineMask.width = symbolConfig.symbolWidth;
        this.mainSpineMask.height = symbolConfig.symbolHeight + 8;

        this.mainSpineMask.anchor.set(0.5);
        this.mainSpineMask.x = symbolConfig.symbolWidth / 2;
        let newYPosition: number = this.mainSpine.y;
        if (
            this.name === UCSymbolNames.F1 ||
            this.name === UCSymbolNames.F2 ||
            this.name === UCSymbolNames.F3 ||
            this.name === UCSymbolNames.F4 ||
            this.name === UCSymbolNames.F5 ||
            this.name === UCSymbolNames.F6 ||
            this.name === UCSymbolNames.F7
        ) {
            newYPosition -= 50;
        }
        this.mainSpineMask.y = newYPosition; //(symbolConfig.symbolHeight * this.size) / 2;
        this.addChild(this.mainSpineMask);

        this.mainSpine.mask = this.mainSpineMask;

        const timeline = gsap.timeline({
            onStart: () => {
                this.topSeparator.alpha = 0;
                if (this.positionInArray === 1) {
                    this.topSeparator.y = 5;
                }
                this.addChild(this.topSeparator);
            },
        });

        const maskAnimation: gsap.core.Tween = gsap.to(this.mainSpineMask, {
            height: this.symbolHeight * this.size,
            duration: animationDuration + 0.1,
            y: (symbolConfig.symbolHeight * this.size) / 2,
        });

        const mainSpineScaleAnimation = gsap.to(this.mainSpine, {
            duration: animationDuration * 0.6,
            pixi: {
                scale: newScale,
            },
            y: (this.symbolHeight * this.size) / 2 + newY,
            x: symbolConfig.symbolWidth / 2 + newX,
        });

        const separatorsAnimation = gsap.to(this.topSeparator, {
            alpha: 1,
            duration: animationDuration,
        });

        timeline.add(maskAnimation, 0);
        timeline.add(mainSpineScaleAnimation, 0);
        timeline.add(separatorsAnimation, 0);

        return timeline;
    }

    public override getWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const tl = gsap.timeline({
            onStart: () => {
                // @ts-ignore
                this.mainSpine.filters = null;
                this.mainSpine.tint = 0xffffff;
            },
        });
        let symbolAnimation: gsap.core.Tween;

        if (
            Object.values(UCLowSymbolNames).includes(this.name as UCLowSymbolNames) ||
            Object.values(UCHighSymbolNames).includes(this.name as UCHighSymbolNames) ||
            this.name === UCSymbolNames.F1 ||
            this.name === UCSymbolNames.F2 ||
            this.name === UCSymbolNames.F3 ||
            this.name === UCSymbolNames.F4 ||
            this.name === UCSymbolNames.F5 ||
            this.name === UCSymbolNames.F6 ||
            this.name === UCSymbolNames.F7 ||
            this.name === UCSymbolNames.BIG_FISH
        ) {
            symbolAnimation = this.mainSpine.getSpineTween(
                this.name === UCSymbolNames.BIG_FISH ? `Win2` : `Win`
            );

            tl.add(symbolAnimation, 0);

            if (
                Object.values(UCLowSymbolNames).includes(this.name as UCLowSymbolNames) ||
                Object.values(UCHighSymbolNames).includes(this.name as UCHighSymbolNames)
            ) {
                const winFrameAnimation = this.winFrameSpine.getSpineTween(`Win`, false, () => {
                    this.winFrameSpine.height = this.winFrameDefaultHeight * this.size;
                    if (this.size === UCSymbolSize.X033) {
                        this.winFrameSpine.height -= 6;
                    }
                    this.winFrameSpine.y = (symbolConfig.symbolHeight * this.size) / 2 + 3;

                    this.addChildAt(this.winFrameSpine, 0);
                });

                tl.add(winFrameAnimation, 0);
            }

            return tl;
        }

        symbolAnimation = gsap.to(this.mainTextureSprite, {
            yoyo: true,
            repeat: 3,
            duration: 0.2,
            pixi: {
                tint: 0xffffff,
                brightness: 2,
            },
        });

        return symbolAnimation;
    }

    public getNoWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        // this.mainSpine.tint = 0x555555;

        const symbolAnimation = gsap.to(this.mainSpine, {
            pixi: { tint: 0x555555 },
            duration: 0.1,
        });

        return symbolAnimation;
    }

    public removeTint(): void {
        this.mainSpine.tint = 0xffffff;
    }

    public override getLandingAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return this.mainSpine.getSpineTween(`Landing`);
    }

    public getMainSpine(): AbstractSpine {
        return this.mainSpine;
    }

    public resetOnStartSpin(): void {
        // @ts-ignore
        this.filters = null;

        this.resetMainSpine(true);
        this.mainSpine.tint = 0xffffff;
        this.resetWinFrameSpine();

        if (this.mainTextureSprite) {
            this.mainTextureSprite.tint = 0xffffff;
            this.mainTextureSprite.alpha = 1;
            // @ts-ignore
            this.mainTextureSprite.filters = null;

            this.addChild(this.mainTextureSprite);
        }
        if (this.size !== UCSymbolSize.X1) {
            this.addChild(this.topSeparator);
        }
        this.alpha = 1;
        this.size = this.initialSize;
        this.isWild = false;
        this.isBlocked = false;
    }

    public override reset(): void {
        // @ts-ignore
        this.filters = null;

        if (this.mainTextureSprite) {
            // @ts-ignore
            this.mainTextureSprite.filters = null;
            this.mainTextureSprite.scale.set(0.5);
            this.mainTextureSprite.x =
                symbolConfig.symbolWidth / 2 - this.mainTextureSprite.width / 2;
            this.mainTextureSprite.y =
                symbolConfig.symbolHeight / 2 - this.mainTextureSprite.height / 2;
            this.mainTextureSprite.mask = null;
        }

        if (this.mainSpineMask) {
            this.removeChild(this.mainSpineMask);
        }

        this.topSeparator.alpha = 0;
        this.topSeparator.y = 0;
        this.removeChild(this.topSeparator);

        this.bottomSeparator.alpha = this.topSeparator.alpha;
        this.bottomSeparator.x = this.topSeparator.x;
        this.removeChild(this.bottomSeparator);

        this.resetMainSpine();
        this.mainSpine.tint = 0xffffff;
        // @ts-ignore
        this.mainSpine.filters = null;
        this.resetWinFrameSpine();

        if (this.mainTextureSprite) {
            this.mainTextureSprite.tint = 0xffffff;
            this.mainTextureSprite.alpha = 1;
            this.addChild(this.mainTextureSprite);
        }

        this.alpha = 1;

        this.scale.set(1);

        this.size = this.initialSize;

        this.isWild = false;

        this.isBlocked = false;
    }

    protected override resetMainSpine(isSpin: boolean = false): void {
        if (this.mainSpine !== undefined && this.name !== UCSymbolNames.MYSTERY) {
            this.mainSpine.reset();

            this.mainSpine.getSpineTween(`Normal`);

            if (!isSpin) {
                this.mainSpine.mask = null;
                this.mainSpine.scale.set(0.5);

                this.mainSpine.x =
                    symbolConfig.symbolWidth / 2 +
                    symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetX!;
                this.mainSpine.y =
                    symbolConfig.symbolHeight / 2 +
                    symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetY!;
            }

            this.addChild(this.mainSpine);
        }
    }

    private resetWinFrameSpine() {
        if (this.winFrameSpine !== undefined) {
            this.winFrameSpine.reset();
            this.removeChild(this.winFrameSpine);
        }
    }
    public getIndiSymWinAmtWaysTl(betWayWinData: IBetWayWinData): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            win = new BitmapText(
                UgliestCatch.apiPlugin.currency.formatValue(betWayWinData.multipliedAmount),
                {
                    fontName: UCFontsNames.UC_DEFAULT,
                    fontSize: 9,
                    tint: 0xffffff,
                }
            ),
            ways = new BitmapText(
                UgliestCatch.apiPlugin.translations.translate(
                    `${betWayWinData.numberOfCombinations} ${
                        betWayWinData.numberOfCombinations > 1 ? `ways` : `way`
                    } won`
                ),
                {
                    fontName: UCFontsNames.UC_WAYS,
                    fontSize: 8,
                }
            ),
            cont: Container = new Container();

        while (win.width > symbolConfig.symbolWidth) {
            win.fontSize--;
        }
        while (ways.width > symbolConfig.symbolWidth) {
            ways.fontSize--;
        }
        win.pivot.set(win.width / 2, win.height / 2);
        ways.pivot.set(ways.width / 2, ways.height / 2);
        ways.position.y = 25;
        cont.addChild(win, ways);
        cont.addChild(ways);
        cont.position.set(symbolConfig.symbolWidth / 2, symbolConfig.symbolHeight / 2);
        tl.add(() => {
            this.addChild(cont);
        });
        tl.to(cont, { duration: 1, y: cont.position.y - 20 });
        tl.add(() => {
            this.removeChild(cont);
        });
        return tl;
    }
}
